import React, { useEffect, useState } from 'react';

import { Autocomplete, Chip, TextField, Typography } from '@mui/material';

import { TypeaheadSearchResult } from '~/services/SearchService';

interface Props {
  onSelect: (selection: TypeaheadSearchResult) => void;
  setValue: (query: string) => void;
  value: string;
  setOpen: (open: boolean) => void;
  open: boolean;
  results: Array<TypeaheadSearchResult>;
  required?: boolean;
  label?: string;
  multiple?: boolean;
  selectedItems?: Array<TypeaheadSearchResult>;
  onRemoveItem?: (item: TypeaheadSearchResult) => void;
  onCurrentValueChange?: (value: TypeaheadSearchResult | Array<TypeaheadSearchResult> | null) => void;
}

export default function TypeaheadSearchForm({
  onSelect,
  value,
  setValue,
  results,
  label = 'Search place',
  onCurrentValueChange,
  required = false,
  multiple = false,
  selectedItems = [],
  onRemoveItem,
}: Props) {
  const [currentValue, setCurrentValue] = useState<TypeaheadSearchResult | null>(null);

  // Pre populate when there is a value in form
  useEffect(() => {
    if (value && !multiple) {
      setCurrentValue({
        id: '',
        format: { mainText: value, secondaryText: '' },
        searchType: '',
        name: value,
      });
    }
  }, [value, multiple]);

  const select = (result: TypeaheadSearchResult | null) => {
    if (!multiple) {
      setCurrentValue(result);
    }

    if (onCurrentValueChange) {
      if (multiple) {
        onCurrentValueChange(selectedItems);
      } else {
        onCurrentValueChange(result);
      }
    }

    if (result !== null) {
      onSelect(result);
    }
  };

  return (
    <Autocomplete<TypeaheadSearchResult, boolean>
      options={results ?? []}
      filterOptions={(x) => x}
      value={multiple ? selectedItems : currentValue}
      inputValue={value}
      noOptionsText="No locations"
      getOptionKey={(option) => option.id}
      getOptionLabel={(option) => option.format.mainText ?? 'Anywhere'}
      multiple={multiple}
      renderInput={(params) => (
        <TextField
          label={label}
          placeholder="Start typing the name of the place"
          required={required}
          name="searchString"
          data-testid="PlaceSearch"
          {...params}
        />
      )}
      renderOption={(props, option: TypeaheadSearchResult) => (
        <li {...props}>
          <Typography>
            {option.format.mainText}{' '}
            <Typography
              component="span"
              variant="body2"
            >{`${option.searchType} | ${option.format.secondaryText}`}</Typography>
          </Typography>
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={index}
            label={option.format.mainText}
            {...getTagProps({ index })}
            onDelete={() => onRemoveItem?.(option)}
          />
        ))
      }
      onInputChange={(_, value: string) => {
        setValue(value);
      }}
      onChange={(_, value) => {
        if (multiple) {
          const newValue = value as Array<TypeaheadSearchResult>;
          if (newValue && newValue.length > selectedItems.length) {
            const lastSelected = newValue[newValue.length - 1];
            onSelect(lastSelected);
          }
        } else {
          select(value as TypeaheadSearchResult | null);
        }
      }}
      fullWidth
    />
  );
}
